import React, { Component } from 'react';
import PropTypes from 'prop-types';
import * as _ from 'lodash';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputBase from '@material-ui/core/InputBase';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CloseIcon from '@material-ui/icons/Close';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import CalendarEdit from 'assets/svg/calendar_edit.svg';
import DoubleTag from 'assets/svg/double_tag.svg';
import Button from 'UI/button';

class OfferModal extends Component {
  constructor(props) {
    super(props);
    const { aiItineraryData } = props;
    const { sellPrice = 0, offer: existingOffer } = aiItineraryData;
    
    // If there's an existing offer, use it; otherwise create a new one
    this.state = {
      offer: existingOffer ? {
        ...existingOffer,
        totalSellPrice: sellPrice,
      } : {
        totalSellPrice: sellPrice,
        offerAmount: undefined,
        expiryDate: moment().add(7, 'days').toISOString(),
        offerName: '',
        customOfferName: '',
        finalSellPrice: sellPrice,
      },
    };
  }

  componentDidUpdate(prevProps) {
    const { isOpen, aiItineraryData } = this.props;
    if (isOpen && !prevProps.isOpen) {
      const { sellPrice = 0, offer: existingOffer } = aiItineraryData;
      // If there's an existing offer, use it; otherwise create a new one
      this.setState({
        offer: existingOffer ? {
          ...existingOffer,
          totalSellPrice: sellPrice,
        } : {
          totalSellPrice: sellPrice,
          offerAmount: undefined,
          expiryDate: moment().add(7, 'days').toISOString(),
          offerName: '',
          customOfferName: '',
          finalSellPrice: sellPrice,
        },
      });
    }
  }

  handleOfferChange = (field, value) => {
    this.setState((prevState) => {      
      const newOffer = { ...prevState.offer, [field]: value };

      // Auto-calculate offer price when offer amount changes
      if (field === 'offerAmount') {
        newOffer.offerAmount = Number(value);
        const { aiItineraryData, showSnackbar } = this.props;
        const { costPrice, sellPrice } = aiItineraryData;
        const margin = sellPrice - costPrice;
        
        if (value > margin) {
          if (showSnackbar) {
            showSnackbar('Offer amount cannot be greater than the total margin', 'error');
          }
          return { offer: prevState.offer };
        }
        newOffer.finalSellPrice = Math.max(0, newOffer.totalSellPrice - Number(value));
      }

      if (field === 'customOfferName') {
        newOffer.customOfferName = value.substring(0, 16).toUpperCase();
      }

      if (field === 'expiryDate') {
        newOffer.expiryDate = `${moment(value).format('YYYY-MM-DD')}T23:59:59.999+0530`;
      }
      
      return { offer: newOffer };
    });
  };

  createOffer = () => {
    const {
      updateItineraryData, showSnackbar, closePricingModal, openPricingModal, aiItineraryData,
    } = this.props;
    let { offer } = this.state;
    
    // Validation
    if (!offer.offerName.trim()) {
      if (showSnackbar) {
        showSnackbar('Please enter an offer name', 'error');
      }
      return;
    }
    
    if (_.isNil(offer.offerAmount) || (offer.offerAmount < 0)) {
      if (showSnackbar) {
        showSnackbar('Please enter a valid offer amount', 'error');
      }
      return;
    }
    
    if (!offer.expiryDate) {
      if (showSnackbar) {
        showSnackbar('Please select an expiry date', 'error');
      }
      return;
    }

    if (offer.offerName === 'custom' && !offer.customOfferName.trim()) {
      if (showSnackbar) {
        showSnackbar('Please enter a custom offer name', 'error');
      }
      return;
    }

    if (offer.offerAmount === 0) { 
      offer = undefined;
    }
    
    updateItineraryData({
      ...aiItineraryData,
      offer,
    });
    
    const isEditing = !_.isEmpty(aiItineraryData.offer);
    showSnackbar(isEditing ? 'Offer updated successfully!' : 'Offer created successfully!', 'success');  
    openPricingModal('sell');
  };

  render() {
    const {
      isOpen, closePricingModal, classes, aiItineraryData,
    } = this.props;
    const { offer } = this.state;
    const isEditing = !_.isEmpty(aiItineraryData.offer);

    return (
      <Dialog
        open={isOpen}
        closePricingModal={closePricingModal}
        maxWidth="sm"
        fullWidth
        classes={{
          paper: classes.offerDialog,
        }}
      >
        <div className={classes.offerDialogHeader}>
          <div className={classes.offerDialogTitleContainer}>
            <div className={classes.offerDialogIcon}>
              <img src={DoubleTag} alt="Offer Pricing" />
            </div>
            <Typography className={classes.offerDialogTitle}>Offer Pricing</Typography>
          </div>
          <CloseIcon className={classes.closeIcon} onClick={closePricingModal} />
        </div>
        
        <div className={classes.offerDialogBody}>
          <div className={classes.offerRow}>
            <div>
              <Typography className={classes.offerLabel}>Total Selling Price</Typography>
              <Typography className={classes.offerValue}>₹{offer.totalSellPrice.toLocaleString()}</Typography>            
            </div>
          </div>

          <div className={classes.offerRow}>
            <div style={{ marginRight: 20 }}>
              <Typography className={classes.offerLabel}>Discount</Typography>
              <InputBase
                className={classes.offerInput}
                startAdornment={<InputAdornment position="start">₹</InputAdornment>}
                type="number"
                value={offer.offerAmount}
                onChange={(e) => this.handleOfferChange('offerAmount', e.target.value)}
                classes={{
                  input: classes.offerInputText,
                }}
              />
            </div>

            <div>
              <Typography className={classes.offerLabel}>Expiry Date</Typography>
              <div className={classes.datePickerContainer}>
                <MuiPickersUtilsProvider utils={MomentUtils}>
                  <img src={CalendarEdit} alt="Calendar" height={20} width={20} />
                  <DatePicker
                    style={{ width: 100, textAlign: 'center' }}
                    disablePast                      
                    format="D MMM YYYY"
                    value={moment().isAfter(offer.expiryDate, 'days') ? null : moment(offer.expiryDate).format('YYYY-MM-DD')}  
                    onChange={(date) => this.handleOfferChange('expiryDate', date)}                      
                  />
                </MuiPickersUtilsProvider>                 
              </div>
            </div>
          </div>

          <div className={classes.offerRow}>
            <div>
              <Typography className={classes.offerLabel}>Offer Name</Typography>
              <div style={{ 
                display: 'flex', 
                flexDirection: 'row', 
                alignItems: 'center',
              }}
              >
                <div className={classes.offerNameContainer}>
                  <Select
                    value={offer.offerName}
                    onChange={(e) => {
                      this.handleOfferChange('offerName', e.target.value);
                      if (e.target.value === 'custom') {
                        setTimeout(() => {
                          document.querySelector('.offerNameInput')?.focus();
                        }, 100);
                      }
                    }}
                    className={classes.offerSelect}
                    displayEmpty
                    renderValue={(value) => value?.split('-').map((word) => _.upperCase(word)).join(' ') || 'Select a offer name'}
                  >
                    <MenuItem value="early-bird-offer">EARLY BIRD OFFER</MenuItem>
                    <MenuItem value="last-minute-saver">LAST MINUTE SAVER</MenuItem>
                    <MenuItem value="flash-sale">FLASH SALE</MenuItem>
                    <MenuItem value="referral-offer">REFERRAL OFFER</MenuItem>
                    <MenuItem value="anniversary-offer">ANNIVERSARY OFFER</MenuItem>
                    <MenuItem value="honeymoon-offer">HONEYMOON OFFER</MenuItem>
                    <MenuItem value="birthday-offer">BIRTHDAY OFFER</MenuItem>
                    <MenuItem value="diwali-deal">DIWALI DEAL</MenuItem>
                    <MenuItem value="christmas-offer">CHRISTMAS OFFER</MenuItem>
                    <MenuItem value="new-year-bonanza">NEW YEAR BONANZA</MenuItem>
                    <MenuItem value="summer-specials">SUMMER SPECIALS</MenuItem>
                    <MenuItem value="loyalty-offer">LOYALTY OFFER</MenuItem>
                    <MenuItem value="monsoon-magic-offer">MONSOON MAGIC OFFER</MenuItem>
                    <MenuItem value="weekend-deal">WEEKEND DEAL</MenuItem>
                    <MenuItem value="group-deal">GROUP DEAL</MenuItem>
                    <MenuItem value="custom">CUSTOM</MenuItem>
                  </Select>                 
                </div>
                {
                  offer.offerName === 'custom' ? (
                    <InputBase
                      className={classes.offerNameInput}
                      value={offer.customOfferName}
                      placeholder="Enter an offer name"
                      onChange={(e) => this.handleOfferChange('customOfferName', e.target.value)}
                    />
                  ) : null
                }
              </div>
            </div>
          </div>

          <div className={classes.offerPriceSection}>
            <Typography className={classes.offerLabel}>Offer Price</Typography>
            <Typography className={classes.offerValue}>₹{offer.finalSellPrice.toLocaleString()}</Typography>
          </div>
          <Button
            onClick={this.createOffer}
            className={classes.createOfferButton}              
            disabled={!offer.offerName || _.isNil(offer.offerAmount) || !offer.expiryDate || (offer.offerName === 'custom' && !offer.customOfferName)}
          >
            {isEditing ? 'Update Offer' : 'Create Offer'}
          </Button>
        </div>
      </Dialog>
    );
  }
}

const styles = (theme) => ({
  offerDialog: {
    borderRadius: 12,    
    minHeight: 500,
    color: theme.colors.black,
  },
  offerDialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 24,
    padding: '40px 40px 20px 40px',
    borderBottom: `1px solid ${theme.colors.border}`,
  },
  offerDialogTitleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  offerDialogIcon: {
    fontSize: 24,
    marginRight: 12,
  },
  offerDialogTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.black,
  },
  closeIcon: {
    cursor: 'pointer',
    color: theme.colors.textDark,
    fontSize: 24,
    '&:hover': {
      color: theme.colors.black,
    },
  },
  offerDialogBody: {
    display: 'flex',
    flexDirection: 'column',
    padding: '0 40px 40px 40px',
    gap: 20,
  },
  offerRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',    
    marginTop: 20,
  },
  offerLabel: {
    fontSize: 14,
    fontWeight: 500,
    color: theme.colors.black,
    marginBottom: 10,    
  },
  offerValue: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  offerInput: {
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 8,
    padding: '12px 16px',
    height: 40,
    width: 160,
    fontSize: 16,
    color: theme.colors.black,
    backgroundColor: theme.colors.white,
    '&:focus-within': {
      borderColor: theme.colors.primary,
    },
  },
  offerInputText: {
    fontSize: 16,
    color: theme.colors.black,
    textAlign: 'end',
    '&::placeholder': {
      color: theme.colors.textLight,
    },
  },
  datePickerContainer: {
    position: 'relative',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 8,
    padding: '5px 10px',
    height: 40,
    width: 160,
    fontSize: 16,
    color: theme.colors.black,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '& input': {
      textAlign: 'center',
    },
    '& :before': {
      borderBottom: 'none !important',
    },
  },
  offerNameContainer: {
    display: 'flex',
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 8,
    padding: '8px 12px',
    backgroundColor: theme.colors.white,
    position: 'relative',
    width: 200,
    '& :before': {
      borderBottom: 'none !important',
    },
    '& :after': {
      borderBottom: 'none !important',
    },
    '& :focus': {
      backgroundColor: 'transparent',
    },
  },
  offerSelect: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: 16,
    '& .MuiSelect-select': {
      border: 'none',
      outline: 'none',
      padding: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  offerPriceSection: {
    marginTop: 20,
  },
  createOfferButton: {       
    width: '150px',
    marginTop: 20,
  },
  offerNameInput: {
    border: `1px solid ${theme.colors.border}`,
    borderRadius: 8,
    padding: '12px 16px',
    height: 40,
    width: 250,
    marginLeft: 10,
  },
});

OfferModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  closePricingModal: PropTypes.func.isRequired,
  updateItineraryData: PropTypes.func,
  aiItineraryData: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  showSnackbar: PropTypes.func,
  openPricingModal: PropTypes.func,
};

export default withStyles(styles)(OfferModal);
